// 3rd Party
import {
    mapGetters,
    mapActions,
} from 'vuex';

// Helpers
import {
    truncateValueToDecimalPlaces,
    formatPrice,
} from '@shared/utils/formatter.js';

// Types
import {
    TransactionType,
} from '@webapp/types/transaction';
import localEpicTypes from '@webapp/store/types.js';

export default {
    computed: {
        ...mapGetters('wallet', [
            'hasRegulatoryRestrictions',
            'regulatoryLimitInfo',
            'hasSendOutRegulatoryRestrictions',
            'sendOutRegulatoryFiatLimitInfo',
        ]),

        ...mapGetters('regulations', ['transactionLimitAgreed']),
    },
    methods: {
        ...mapActions({
            setModal: localEpicTypes.SET_MODAL,
            resetModal: localEpicTypes.RESET_MODAL,
        }),

        ...mapActions('wallet', ['fetchWalletRemainingLedger']),

        ...mapActions('regulations', ['setTransactionLimitAgreed']),

        /**
         * Check if the Regulatory Restriction modal needs to show and also fetch data if needed.
         *
         * @param {object} payload Regulatory transaction (name, store check, fetching data)
         * @param {string} payload.transactionName Transaction names
         * @param {boolean} payload.isSendOutRegulatoryRestriction Whether it is a regulatory restriction
         * @param {object} payload.withFetch Whether to fetch wallet remaining ledger
         * @returns {void}
         */
        async checkRegulatoryRestrictionLimit({
            transactionName,
            isSendOutRegulatoryRestriction = false,
            withFetch = {
                doFetch: false,
                batchType: 'buy',
                movementType: 'credit',
            },
        }) {
            if (withFetch.doFetch) {
                await this.fetchWalletRemainingLedger({
                    batchType: withFetch.batchType,
                    movementType: withFetch.movementType,
                });
            }

            const isRegulatoryRestrictionsAvailable = isSendOutRegulatoryRestriction
                ? this.hasSendOutRegulatoryRestrictions
                : this.hasRegulatoryRestrictions;

            if (!isRegulatoryRestrictionsAvailable || this.transactionLimitAgreed[transactionName]) {
                return;
            }

            this.setRegulatoryRestrictionModal(transactionName);
        },

        /**
         * Set the Text and limit amounts for the regulatory restriction modal and opens it.
         *
         * @param {TransactionType} transactionName Transaction Name
         * @param {boolean} addButtons Add agree and disagree buttons
         * @returns {void}
         */
        setRegulatoryRestrictionModal(transactionName: TransactionType, addButtons = true) {
            // Get Regulatory Limit Amounts
            const limitInfo = transactionName === TransactionType.Withdrawal ? this.sendOutRegulatoryFiatLimitInfo : this.regulatoryLimitInfo;
            const limitAmount = truncateValueToDecimalPlaces(limitInfo.limitAmount);
            const dailyLimit = formatPrice(limitAmount.toString(), limitInfo.currencySymbol);

            const title: string = this.$t('regulatoryRestrictionSheet.title', {
                action: this.$t(`regulatoryRestrictionSheet.${transactionName}`),
            });

            let description: string = this.$t('regulatoryRestrictionSheet.description', {
                action: this.$t(`regulatoryRestrictionSheet.${transactionName}`),
                limit: dailyLimit,
            });

            if (transactionName === TransactionType.Receive) {
                description += this.$t('regulatoryRestrictionSheet.receiveDescription', {
                    limit: dailyLimit,
                });
            }

            this.setModal({
                isVisible: true,
                isClosable: !addButtons,
                image: require('@webapp/assets/info.webp'),
                title,
                messages: [
                    {
                        text: description,
                    },
                ],
                ...addButtons
                    ? {
                        acceptButton: {
                            text: this.$t('regulatoryRestrictionSheet.agree'),
                            action: () => {
                                this.setTransactionLimitAgreed({
                                    transactionName,
                                    value: true,
                                });
                                this.resetModal();
                            },
                        },
                        cancelButton: {
                            text: this.$t('regulatoryRestrictionSheet.disagree'),
                            action: () => {
                                this.$router.back();
                                setTimeout(() => {
                                    this.resetModal();
                                });
                            },
                        },
                    } : {},
            });
        },
    },
};